import type { BrowserSessionJwt, TeamJwt } from '../auth/teamJwt';
import { consoleConfig } from '../config';

/**
 * The worker's connect endpoints (contract rev 10): an agent's OAuth
 * authorization and the CLI's device code, both approved by a signed-in member.
 * The approval carries the browser's current org JWT in the body; the server
 * verifies it and reads the member's email, so any of the user's orgs will do.
 */

type ApprovalJwt = BrowserSessionJwt | TeamJwt;

export interface ConnectDescription {
  /** The registered client, e.g. "Claude Code"; null when it gave no name. */
  clientName: string | null;
  /** Where the code goes: the client's loopback host and port. */
  redirectHost: string;
}

/**
 * A refusal from a connect endpoint. `code` is the server's `error` (for
 * example `expired_txn`, `invalid_session`); the page words it, never shows it.
 */
export class ConnectError extends Error {
  readonly status: number;
  readonly code: string | null;

  constructor(status: number, code: string | null) {
    super(code ? `Connect request failed: ${code}` : `Connect request failed with ${status}`);
    this.name = 'ConnectError';
    this.status = status;
    this.code = code;
  }
}

async function post<T>(path: string, body: Record<string, unknown>): Promise<T> {
  const response = await fetch(`${consoleConfig.collabApiBaseUrl}${path}`, {
    method: 'POST',
    credentials: 'omit',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const parsed = await response.json().catch(() => null) as { error?: unknown } | null;
    throw new ConnectError(response.status, typeof parsed?.error === 'string' ? parsed.error : null);
  }
  return response.json() as Promise<T>;
}

export function describeConnect(txn: string): Promise<ConnectDescription> {
  return post('/oauth/connect/describe', { txn });
}

export async function approveConnect(txn: string, sessionJwt: ApprovalJwt): Promise<{ redirectTo: string }> {
  const data = await post<{ redirectTo?: unknown }>('/oauth/connect/approve', { txn, sessionJwt });
  if (typeof data.redirectTo !== 'string' || !data.redirectTo) {
    throw new ConnectError(502, null);
  }
  return { redirectTo: data.redirectTo };
}

export async function approveDevice(userCode: string, sessionJwt: ApprovalJwt): Promise<void> {
  await post('/oauth/device/approve', { userCode, sessionJwt });
}
